import type { TooltipProps } from "@mui/material";
import { Box, Stack, Tooltip } from "@mui/material";
import type { PropsWithChildren } from "react";
import { Link } from "react-router";
import type { AccessPermission } from "@entrilia/shared/api/types";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import ArtifactPreviewImage from "../previews/ArtifactPreviewImage";
import ArtifactFavoriteButton from "./ArtifactFavoriteButton";
import ArtifactPreviewCard from "./ArtifactPreviewCard";
import type { ArtifactRow } from "./artifactRow";

const previewCardSlotProps: TooltipProps["slotProps"] = {
  tooltip: {
    sx: {
      width: 360,
      maxWidth: 360,
      p: 1,
      pb: 1.5,
      bgcolor: "background.paper",
      color: "text.primary",
      border: 1,
      borderColor: "divider",
      borderRadius: 2,
      boxShadow: 8,
    },
  },
};

interface Props {
  artifact: ArtifactRow;
  href: string;
  permissions: AccessPermission[];
  canManage: boolean;
  isGroupSelected: boolean;
  onToggleFavorite: () => void;
}

const ArtifactNameCell = ({
  artifact,
  href,
  permissions,
  canManage,
  isGroupSelected,
  onToggleFavorite,
  children,
}: PropsWithChildren<Props>) => (
  <Stack
    direction="row"
    sx={{ alignItems: "center", position: "relative", flex: 1, height: "100%", minWidth: 0, overflow: "hidden" }}
  >
    <AuthorizedBox permissions={permissions}>{children}</AuthorizedBox>
    <Stack
      component={Link}
      to={href}
      direction="row"
      className="highlighted-action"
      sx={{
        alignItems: "center",
        flex: 1,
        height: "100%",
        minWidth: 0,
        color: "text.primary",
        textDecoration: "none",
        overflow: "hidden",
      }}
    >
      <Tooltip
        title={<ArtifactPreviewCard artifact={artifact} canManage={canManage} />}
        placement="right"
        enterDelay={300}
        enterNextDelay={300}
        disableInteractive
        slotProps={previewCardSlotProps}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 64,
            ml: isGroupSelected ? 1 : 5.3,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <ArtifactPreviewImage artifact={artifact} loadOnMount />
        </Box>
      </Tooltip>
      <TypographyTooltipEllipsis text={artifact.name} typographyProps={{ sx: { pl: 1.25 } }} />
    </Stack>
    <ArtifactFavoriteButton
      isFavorite={artifact.isFavorite}
      className={artifact.isFavorite ? undefined : "favorite-star"}
      onClick={onToggleFavorite}
    />
  </Stack>
);

export default ArtifactNameCell;
